import { onJQueryTriggeredChange } from "#/admin/legacy/jquery-change-bridge.js";
import { onDocumentReady } from "#/admin/legacy/forms/document-ready.js";
import { disableInputs, enableInputs, findInputs } from "#/admin/legacy/forms/input-ownership.js";
import { findTargets } from "#/admin/legacy/forms/target-lookup.js";
import { setTargetVisibility } from "#/admin/legacy/forms/target-visibility.js";

// A control is a native `<select>` or an `<sp-select>`. The option-level rules sit on a native
// option's `data-*` attributes and on an sp-select item's `dataset`.
var CONTROL_SELECTOR = "select, sp-select";
var SELECT_SELECTOR = 'select[data-behaviour~="show-on-select"], sp-select[data-behaviour~="show-on-select"]';
var OPTION_SELECTOR = 'option[data-behaviour~="show-on-select"]';

// Returns the option-level targets of a control as `{ dataset, selected }` records, in option order.
function optionTargets(control) {
  if (control.tagName === "SP-SELECT") {
    var value = control.value;
    var values = [].concat(value === null || value === undefined ? [] : value).map(String);

    return flattenItems(control.items || [])
      .filter(function (item) {
        return hasBehaviour(item.dataset, "show-on-select");
      })
      .map(function (item) {
        return {
          dataset: item.dataset,
          selected: values.includes(String(item.value)),
        };
      });
  }

  return Array.from(control.querySelectorAll(OPTION_SELECTOR)).map(function (option) {
    return {
      dataset: option.dataset,
      selected: option.selected,
    };
  });
}

function flattenItems(items) {
  return items.flatMap(function (item) {
    return [item].concat(flattenItems(item.children || []));
  });
}

function hasBehaviour(dataset, behaviour) {
  return String((dataset && dataset.behaviour) || "")
    .split(/\s+/)
    .includes(behaviour);
}

// Returns the controls in `container` carrying an option-level target, none without a container.
export function controlsWithOptionTargets(container) {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(CONTROL_SELECTOR)).filter(function (control) {
    return optionTargets(control).length > 0;
  });
}

export function initializeSelectTargets(control) {
  if (hasBehaviour(control.dataset, "show-on-select")) {
    toggleSelectTargets(control);
  }

  toggleSelectOptionTargets(control);
}

function handleSelectTargetsChange(event) {
  const control = event.target.closest(CONTROL_SELECTOR);

  if (!control) {
    return;
  }

  if (control.matches(SELECT_SELECTOR)) {
    toggleSelectTargets(control);
  }

  toggleSelectOptionTargets(control);
}

document.addEventListener("change", handleSelectTargetsChange);
document.addEventListener("input", handleSelectTargetsChange);

onJQueryTriggeredChange(handleSelectTargetsChange);
onDocumentReady(function initializeOptionTargets() {
  controlsWithOptionTargets(document).forEach(initializeSelectTargets);
});

function toggleSelectTargets(select) {
  var rule = {
    dataset: select.dataset,
    control: select,
  };

  toggleTargets(rule, rule.dataset.showTarget, hasSelection(select.value));
}

// Zero and false are selected values. An array is selected when any member is selected.
function hasSelection(value) {
  return Array.isArray(value) ? value.some(hasSelection) : value !== null && value !== undefined && value !== "";
}

// NOTE: This function skips options that have the same targeting data attributes as ones that have already been processed.
//       If the data-attributes change then the cache key template string should be updated
function toggleSelectOptionTargets(control) {
  const processed = new Set();
  const selected = [];

  optionTargets(control).forEach(function (option) {
    var rule = {
      dataset: option.dataset,
      control: control,
    };
    var key = `${rule.dataset.showScope}|${rule.dataset.showTarget}|${rule.dataset.hideTarget}`;

    if (option.selected) {
      selected.push(rule);
    }

    if (processed.has(key)) {
      return; // already handled this combo
    }

    toggleTargets(rule, rule.dataset.showTarget, false);
    toggleTargets(rule, rule.dataset.hideTarget, true);

    processed.add(key);
  });

  // show selected options as a second pass so that we ensure nodes with multiple targets are shown
  selected.forEach(function (rule) {
    toggleTargets(rule, rule.dataset.showTarget, true);
    toggleTargets(rule, rule.dataset.hideTarget, false);
  });
}

// Shows or hides the elements `selector` names, and unless the rule keeps its inputs enabled,
// enables or disables their inputs along with it. A rule is `{ dataset, control }`: the `data-*`
// attributes naming the targets, and the control they were read from, which scopes the targets
// and identifies who disabled an input.
function toggleTargets(rule, selector, visible) {
  const targets = findTargets(rule.control, selector, rule.dataset.showScope);

  for (const target of targets) {
    setTargetVisibility(target, visible);

    if (!togglesInputs(rule)) {
      continue;
    }

    if (visible) {
      enableInputs(findInputs(target), rule.control);
    } else {
      disableInputs(findInputs(target), rule.control);
    }
  }
}

// A disabled input is not submitted, so a hidden target's values are left out of the save. That is
// what a target holding fields for a different record should do; where the hidden fields belong to
// the record being saved, `data-keep-inputs-enabled` keeps an edit made before switching away.
function togglesInputs(rule) {
  var keep = rule.dataset.keepInputsEnabled;

  return keep === undefined || keep === "false";
}
